// ieee-applicationstate.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ASMEApplicationData } from '../../../../shared/models/asme-application-data';

@Injectable({
  providedIn: 'root'
})
export class ASMEApplicationStateService {
  private readonly STORAGE_KEY = 'asme_application_data';
  
  // Track dynamically if needed by the wizard components
  public showComputerPage = false; 
  public showClaimSupplementPage = false;
  public showFloridaFollowUp = false;
  public emailTrackId?: number;

  private masterDataSubject = new BehaviorSubject<ASMEApplicationData>({});
  public masterData$: Observable<ASMEApplicationData> = this.masterDataSubject.asObservable();

  constructor() {
    this.restoreFromStorage();
  }

  getLatestState(): ASMEApplicationData {
    return this.masterDataSubject.getValue();
  }

  getStepData<T = any>(stepKey: keyof ASMEApplicationData): T | undefined {
    return this.getLatestState()[stepKey] as T | undefined;
  }

  updateStepData(stepKey: keyof ASMEApplicationData, partialData: any): void {
    const freshState = {
      ...this.getLatestState(),
      [stepKey]: partialData
    };
    
    this.masterDataSubject.next(freshState);
    this.saveToStorage(freshState);
  }

  patchStepData(stepKey: keyof ASMEApplicationData, partialData: Record<string, any>): void {
    const currentState = this.getLatestState();
    const existingStepData = (currentState[stepKey] ?? {}) as Record<string, any>;

    const freshState = {
      ...currentState,
      [stepKey]: {
        ...existingStepData,
        ...partialData
      }
    };

    this.masterDataSubject.next(freshState);
    this.saveToStorage(freshState);
  }

 getFinalSubmissionPayload(fileMetadata: any[], flatBinaries: File[]): any {
  const collectedData = this.getLatestState();

  // Combine binaries with their corresponding attachment code and custom name
  const packagedFiles = flatBinaries.map((file, index) => {
    const meta = fileMetadata[index] || {};
    return {
      file: file,                        
      fileName: file.name,               
      attCode: meta.attCode || 'DEFAULT' 
    };
  });
  return {
    formData: {
      welcome: collectedData.welcome || null,
      coverage: collectedData.coverage || null,
      entityType: collectedData.entityType || null,   
      revenue: collectedData.revenue || null,
      practice: collectedData.practice || null,
      projectsContracts: collectedData.projectsContracts || null,
      priorInsurance: collectedData.priorInsurance || null,
      claimLincensing: collectedData.claimsLicensing || null,
      claimInfoSupplement: collectedData.claimInfoSupplement || null,
      riskPracticeQuestionsOne: collectedData.riskPracticeQuestionsOne || null,
      riskPracticeQuestionsTwo: collectedData.riskPracticeQuestionsTwo || null,
      reviewSign: collectedData.reviewSign || null
      
      
    },
    files: packagedFiles 
  };
}


  clearApplicationSession(): void {
    const clearedState: ASMEApplicationData = {};
    this.showComputerPage = false;
    this.showClaimSupplementPage = false;
    this.showFloridaFollowUp = false;
    this.emailTrackId = undefined;
    this.masterDataSubject.next(clearedState);
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(clearedState));
    } catch (error) {
      console.warn('Unable to clear session storage.', error);
    }
  }

  private saveToStorage(data: ASMEApplicationData): void {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Unable to persist form state.', error);
    }
  }

  private restoreFromStorage(): void {
    try {
      const savedProgress = sessionStorage.getItem(this.STORAGE_KEY);
      if (savedProgress) {
        this.masterDataSubject.next(JSON.parse(savedProgress));
      }
    } catch (error) {
      console.warn('Unable to restore form state.', error);
    }
  }
}